/**
 * Cart extras: gift note, and terms acceptance.
 *
 * The two features are independent. Each is driven by its own setting and its own
 * markup, and neither reads the other's state. They meet in exactly one place —
 * the checkout interception at the bottom of this file — because both need to act
 * before the shopper leaves the page.
 *
 * Markup: snippets/cart-gift-note.liquid, snippets/cart-terms.liquid
 *
 * Listeners are delegated on document because the cart section is destroyed and
 * rebuilt on every ajax refresh (theme.js swaps [data-section-type="cart-template"]
 * after an ajax add, and assets/cart-gift-box.js does the same). State is re-read
 * from the freshly rendered Liquid each time rather than tracked in JS.
 */
(function () {
  'use strict';

  if (window.ccCartExtrasInitialised) return;
  window.ccCartExtrasInitialised = true;

  var NOTE_ROOT = '[data-gift-note]';
  var NOTE_INPUT = '[data-gift-note-input]';
  var NOTE_TOGGLE = '[data-gift-note-toggle]';
  var NOTE_COUNTER = '[data-gift-note-counter]';
  var NOTE_STATUS = '[data-gift-note-status]';

  var SAVE_DELAY = 500;
  var WARN_AT = 0.9;
  var SAVED_VISIBLE_FOR = 2000;

  var TERMS_ROOT = '[data-cart-terms]';
  var TERMS_CHECKBOX = '[data-cart-terms-checkbox]';
  var TERMS_ERROR = '[data-cart-terms-error]';
  var CHECKOUT_BTN = '#cartform [name="checkout"]';

  var TERMS_ATTRIBUTE = 'Terms Accepted';
  var TERMS_VALUE = 'Yes';

  var noteTimer = null;
  var notePending = false;
  var noteInFlight = null;
  var savedTimer = null;
  var resubmitting = false;
  var termsInFlight = null;

  function noteRoot() {
    return document.querySelector(NOTE_ROOT);
  }

  function termsRoot() {
    return document.querySelector(TERMS_ROOT);
  }

  function termsAccepted() {
    var box = document.querySelector(TERMS_CHECKBOX);
    return !!(box && box.checked);
  }

  function postAttributes(url, attributes) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (response) {
      if (!response.ok) throw new Error('');
      return response.json();
    });
  }

  function setStatus(root, text, isError) {
    var el = root.querySelector(NOTE_STATUS);
    if (!el) return;

    clearTimeout(savedTimer);

    if (!text) {
      el.textContent = '';
      el.hidden = true;
      return;
    }

    el.textContent = text;
    el.classList.toggle('cart-gift-note__status--error', !!isError);
    el.hidden = false;

    if (!isError) {
      savedTimer = setTimeout(function () {
        var current = noteRoot();
        var node = current && current.querySelector(NOTE_STATUS);
        if (node) {
          node.textContent = '';
          node.hidden = true;
        }
      }, SAVED_VISIBLE_FOR);
    }
  }

  function updateCounter(root) {
    var input = root.querySelector(NOTE_INPUT);
    var counter = root.querySelector(NOTE_COUNTER);
    if (!input || !counter) return;

    var limit = parseInt(root.dataset.limit, 10) || 200;
    var used = input.value.length;

    counter.textContent = used + '/' + limit;
    counter.classList.toggle('is-warning', used >= Math.floor(limit * WARN_AT));
  }

  function saveNote(root) {
    var input = root.querySelector(NOTE_INPUT);
    if (!input) return Promise.resolve();

    notePending = false;

    noteInFlight = postAttributes(root.dataset.cartUpdateUrl, { 'gift-note': input.value })
      .then(function () {
        setStatus(noteRoot() || root, root.dataset.savedText, false);
      })
      .catch(function () {
        setStatus(noteRoot() || root, root.dataset.errorText, true);
      })
      .then(function () {
        noteInFlight = null;
      });

    return noteInFlight;
  }

  /**
   * Writes any outstanding note immediately and resolves once it has landed.
   *
   * This is the fix for the classic failure in this feature: type, then click
   * checkout inside the debounce window, and the last characters never reach the
   * cart. Callers await this before letting a checkout proceed.
   */
  function flushNote() {
    var root = noteRoot();
    if (!root) return Promise.resolve();

    if (noteTimer) {
      clearTimeout(noteTimer);
      noteTimer = null;
    }

    if (notePending) return saveNote(root);

    return noteInFlight || Promise.resolve();
  }

  function hasPendingWork() {
    return notePending || noteTimer !== null || noteInFlight !== null || termsInFlight !== null;
  }

  function flushAll() {
    return Promise.all([flushNote(), termsInFlight || Promise.resolve()]);
  }

  /* ---------------------------------------------------------------------------
     Terms acceptance
     ------------------------------------------------------------------------ */

  function showTermsError(message) {
    var root = termsRoot();
    if (!root) return;

    var el = root.querySelector(TERMS_ERROR);
    if (el) el.textContent = message || '';
  }

  function syncCheckoutButton() {
    var button = document.querySelector(CHECKOUT_BTN);
    if (!button) return;

    // No terms block on the page means this feature is off; never interfere.
    if (!termsRoot()) {
      button.removeAttribute('aria-disabled');
      return;
    }

    if (termsAccepted()) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * Records acceptance as a cart attribute.
   *
   * On failure the checkbox is put back to its previous state. That is what keeps
   * the record and the UI in agreement: if the write did not land, the shopper is
   * not treated as having accepted, and the gate below stops them.
   */
  function saveTerms(checkbox) {
    var root = termsRoot();
    if (!root) return Promise.resolve();

    var wanted = checkbox.checked;
    var attributes = {};
    attributes[TERMS_ATTRIBUTE] = wanted ? TERMS_VALUE : '';

    termsInFlight = postAttributes(root.dataset.cartUpdateUrl, attributes)
      .then(function () {
        showTermsError('');
      })
      .catch(function () {
        checkbox.checked = !wanted;
        showTermsError(root.dataset.saveErrorText || root.dataset.errorText);
      })
      .then(function () {
        termsInFlight = null;
        syncCheckoutButton();
      });

    return termsInFlight;
  }

  function termsBlocks() {
    if (!termsRoot()) return false;
    if (termsAccepted()) return false;

    var root = termsRoot();
    showTermsError(root.dataset.errorText);

    var box = document.querySelector(TERMS_CHECKBOX);
    if (box) box.focus();

    return true;
  }

  document.addEventListener('input', function (event) {
    var input = event.target.closest && event.target.closest(NOTE_INPUT);
    if (!input) return;

    var root = input.closest(NOTE_ROOT);
    if (!root) return;

    updateCounter(root);
    setStatus(root, '', false);

    notePending = true;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      noteTimer = null;
      saveNote(root);
    }, SAVE_DELAY);
  });

  document.addEventListener('change', function (event) {
    var toggle = event.target.closest && event.target.closest(NOTE_TOGGLE);
    if (!toggle) return;

    var root = toggle.closest(NOTE_ROOT);
    if (!root) return;

    var field = root.querySelector('.cart-gift-note__field');
    if (!field) return;

    field.hidden = !toggle.checked;

    if (toggle.checked) {
      var input = root.querySelector(NOTE_INPUT);
      if (input) input.focus();
      return;
    }

    // Closing the toggle clears the note, so the cart never carries a message
    // the shopper has hidden and believes they have removed.
    var textarea = root.querySelector(NOTE_INPUT);
    if (textarea && textarea.value !== '') {
      textarea.value = '';
      updateCounter(root);
      notePending = true;
      flushNote();
    }
  });

  document.addEventListener('change', function (event) {
    var checkbox = event.target.closest && event.target.closest(TERMS_CHECKBOX);
    if (!checkbox) return;

    showTermsError('');
    syncCheckoutButton();
    saveTerms(checkbox);
  });

  // A link inside the label would otherwise toggle the checkbox on its way out.
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('.cart-terms__text a');
    if (link) event.stopPropagation();
  });

  /* ---------------------------------------------------------------------------
     Shared checkout interception — the only place the two features meet
     ------------------------------------------------------------------------ */

  // Primary gate. Runs on the click so the shopper gets an explanation, which a
  // natively disabled button could not provide.
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest(CHECKOUT_BTN);
    if (!button) return;

    if (termsBlocks()) {
      event.preventDefault();
      event.stopPropagation();
    }
  });

  /**
   * Backstop gate plus the pending-save flush.
   *
   * Any outstanding write is flushed before the form is allowed through, then the
   * submission is replayed with its original submitter so checkout-vs-update is
   * preserved. Only the checkout button is gated — the Update button must keep
   * working whether or not terms have been accepted.
   */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.id !== 'cartform') return;
    if (resubmitting) return;

    var submitter = event.submitter || null;
    var isCheckout = !!(submitter && submitter.name === 'checkout');

    if (isCheckout && termsBlocks()) {
      event.preventDefault();
      return;
    }

    if (!hasPendingWork()) return;

    event.preventDefault();

    flushAll().then(function () {
      resubmitting = true;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter || undefined);
      } else if (submitter) {
        submitter.click();
      } else {
        form.submit();
      }
      resubmitting = false;
    });
  });

  /**
   * Fallback for exits we cannot intercept — wallet buttons render inside
   * shadow DOM and their clicks are not reachable, and a closed tab fires no
   * submit at all. sendBeacon survives the page going away.
   */
  window.addEventListener('pagehide', function () {
    if (!notePending) return;

    var root = noteRoot();
    var input = root && root.querySelector(NOTE_INPUT);
    if (!input || !navigator.sendBeacon) return;

    var body = new Blob(
      [JSON.stringify({ attributes: { 'gift-note': input.value } })],
      { type: 'application/json' }
    );
    navigator.sendBeacon(root.dataset.cartUpdateUrl, body);
  });

  function init() {
    var root = noteRoot();
    if (root) updateCounter(root);

    // Liquid already renders the correct initial state, so this only re-asserts
    // it after an ajax cart swap. With no terms block present it is a no-op.
    syncCheckoutButton();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
